import { StyleSheet, type TextStyle } from 'react-native'
import { SvgXml } from 'react-native-svg'

import { useFunkitTranslation } from '../providers/FunkitI18nProvider'
import { Box, Text, useTheme } from '../theme/restyle'

const LOGO_WIDTH = 36
const LOGO_HEIGHT = 15

// The Fun wordmark, exported verbatim from the Fun Mobile MVP file
// (`AttributionWatermark` / `fun-logo`, node I2:1282;226:862). The export bakes
// the watermark alpha into each fill; swapped to `currentColor` so the
// `content/watermark` token carries it instead and the mark tracks the theme.
const FUN_LOGO_SVG =
  '<svg width="36" height="15" viewBox="0 0 36 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.29538 0V15L11.4879 11.25V3.75L4.29538 0Z" fill="currentColor"/><path d="M2.14907 0V15L3.22337 14.4396V0.560417L2.14907 0Z" fill="currentColor"/><path d="M0 0V15L1.0743 14.4396V0.560417L0 0Z" fill="currentColor"/><path d="M32.0036 11.6212H30.5985V6.04405H31.8999L32.0151 6.7666C32.3721 6.20211 33.0632 5.8747 33.8348 5.8747C35.2629 5.8747 36 6.74402 36 8.18911V11.6212H34.5949V8.51652C34.5949 7.57946 34.1227 7.12787 33.3971 7.12787C32.5334 7.12787 32.0036 7.71494 32.0036 8.61812V11.6212Z" fill="currentColor"/><path d="M27.8798 6.04371H29.2849V11.6209H27.9835L27.8798 10.8757C27.5343 11.4064 26.7972 11.7676 26.0371 11.7676C24.7241 11.7676 23.9525 10.8983 23.9525 9.53225V6.04371H25.3576V9.04679C25.3576 10.108 25.7837 10.537 26.5669 10.537C27.4537 10.537 27.8798 10.029 27.8798 8.96777V6.04371Z" fill="currentColor"/><path d="M22.5515 8.24533H19.4189V11.621H17.9447V3.36814H23.1504V4.70033H19.4189V6.947H22.5515V8.24533Z" fill="currentColor"/></svg>'

/**
 * "powered by Fun" at the foot of the flow (Figma: `AttributionWatermark`, node
 * 2:1282). Set in `content/watermark` — legible enough to find, quiet enough to
 * ignore — and hidden entirely when the host sets `hideAttribution`.
 *
 * Absolutely positioned, so it never enters the layout of the screen it sits
 * under; a screen renders it last and pays it no space.
 */
export function AttributionWatermark() {
  const { t } = useFunkitTranslation()
  const theme = useTheme()
  const color = theme.content.elements.watermark

  return (
    <Box
      flexDirection="row"
      alignItems="center"
      justifyContent="center"
      pointerEvents="none"
      style={styles.row}
    >
      <Text weight="medium" style={[styles.label, { color }]}>
        {t('common.poweredBy')}
      </Text>
      <SvgXml
        xml={FUN_LOGO_SVG.replace(/currentColor/g, color)}
        width={LOGO_WIDTH}
        height={LOGO_HEIGHT}
      />
    </Box>
  )
}

const styles = StyleSheet.create({
  // 16 above the screen's bottom edge, inside the home indicator's band.
  row: { position: 'absolute', bottom: 16, left: 0, right: 0, gap: 5 },
  // The mark's own type, not a body token: it is set with the wordmark, and
  // scaling it with a host's font size would break their pairing.
  label: {
    fontSize: 11,
    lineHeight: 18,
    letterSpacing: -0.66,
  } as TextStyle,
})
